import Glider from 'glider-js';

class Carousel {

   /**
     * @public
     * Sets the container value to the DOM element that contains the whole Slider Carousel.
     * 
     * @param { string }
     */
  setContainer(containerSelector) {
    this.container = document.querySelector(containerSelector);
    return this;
  }

  /**
     * @public
     * Sets the number of slides to show in the Slider Carousel container.
     * 
     * @param { number }
     */
  setSlidesToShow(slidesToShow) {
    this.slidesToShow = slidesToShow;
    return this;
  }

  /**
     * @public
     * Sets a confinguration to make draggable the carousel.
     * 
     * @param { string }
     */
  setDraggable(draggable) {
    this.draggable = draggable;
    return this;
  }

  /**
     * @public
     * Sets an object containing the prev arrow settings.
     * 
     * @param { string }
     */
  setLeftArrow(leftArrowSelector) {
    this.leftArrow = leftArrowSelector;
    return this;
  }

  /**
     * @public
     * Sets an object containing the next arrow settings.
     * 
     * @param { string }
     */
  setRightArrow(rightArrowSelector) {
    this.rightArrow = rightArrowSelector;
    return this;
  }

  /**
     * @public
     * Sets an object containing custom settings per provided breakpoint.
     * 
     * @param { Array }
     */
  setResposive(responsive) {
    this.responsive = responsive;
    return this;
  }

  /**
     * @public
     * Sets a confinguration to skip the auto track wrapping of the carousel.
     * 
     * @param { Bool }
     */
  setSkipTrack(skipTrack) {
    this.skipTrack = skipTrack;
    return this;
  }

  /**
     * @public
     * Sets a configuration to make Glider snap to the nearest slide after any
     * scroll interaction (e.g. trackpad/wheel scrolling), instead of leaving
     * it stopped mid-slide.
     *
     * @param { Bool }
     */
  setScrollLock(scrollLock) {
    this.scrollLock = scrollLock;
    return this;
  }

  /**
     * @public
     * Sets the delay (in milliseconds) Glider waits before snapping to the
     * nearest slide once `scrollLock` is enabled. Only takes effect when
     * scrollLock is set to true.
     *
     * @param { number }
     */
  setScrollLockDelay(scrollLockDelay) {
    this.scrollLockDelay = scrollLockDelay;
    return this;
  }

  /**
     * @public
     * Method to hide each arrow independently based on Glider's own per-direction disabled state
     * (the `disabled` class it toggles on scroll — see glider.js's updateControls), and keep that
     * in sync as the user navigates: on the first slide only the prev arrow hides, on the last
     * only the next arrow hides, and both hide when there's nothing to scroll to at all (single
     * slide). Re-synced on every 'scroll' so it also covers going from an end back to the middle.
     */
  hideDisabledArrows() {
    if (!this.container || !this.leftArrow || !this.rightArrow) {
      return this;
    }

    const prevElement = document.querySelector(this.leftArrow);
    const nextElement = document.querySelector(this.rightArrow);

    if (!prevElement || !nextElement) {
      return this;
    }

    const syncArrowVisibility = () => {
      prevElement.hidden = prevElement.classList.contains('disabled');
      nextElement.hidden = nextElement.classList.contains('disabled');
    };

    syncArrowVisibility();
    this.container.addEventListener('scroll', syncArrowVisibility);

    return this;
  }

  /**
     * @public
     * Method to create the new Glider instance.
     */
  create() {
    if (this.container) {
      this.carousel = new Glider(this.container, {
          slidesToShow: this.slidesToShow,
          draggable: this.draggable,
          arrows: {
            prev: this.leftArrow,
            next: this.rightArrow
          },
          responsive: this.responsive,
          skipTrack: this.skipTrack,
          scrollLock: this.scrollLock,
          scrollLockDelay: this.scrollLockDelay,
      });
    }

    return this;
  }

  /**
     * @public
     * Method to refresh the Glider instance.
     */
  refresh() {
    if (this.carousel) {
      this.carousel.refresh(true);
      this.carousel.scrollItem(0);
    }

    return this;
  }
}

export default Carousel;